export type ScheduledReportFrequency =
  | 'WEEKLY'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'SEMI_ANNUAL'
  | 'YEARLY';

export type ScheduledReportRuleInput = {
  id: string;
  frequency: ScheduledReportFrequency;
  daysOfWeek?: number[];
  dayOfMonth?: number | null;
  lastDayOfMonth?: boolean | null;
  fiscalYearStartMonth?: number | null;
  yearlyMonth?: number | null;
  yearlyDay?: number | null;
  hour: number;
  timezone: string;
  lastRunAt?: Date | null;
  nextRunAt?: Date | null;
};

export type ScheduledReportRuleWithNextRun = ScheduledReportRuleInput & {
  nextRunAt: Date | null;
};

type ZonedDateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  dayOfWeek: number;
};

const WEEKDAY_MAP: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const MAX_SEARCH_DAYS = 400;
const MAX_YEARLY_SEARCH_YEARS = 8;

function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat('en-US', { timeZone: timezone });

    return true;
  } catch {
    return false;
  }
}

function resolveTimezone(timezone: string): string {
  return isValidTimezone(timezone) ? timezone : 'UTC';
}

function getZonedParts(date: Date, timezone: string): ZonedDateParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'short',
    hourCycle: 'h23',
  });

  const parts = formatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';

  const hourRaw = Number(get('hour'));

  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: hourRaw === 24 ? 0 : hourRaw,
    minute: Number(get('minute')),
    second: Number(get('second')),
    dayOfWeek: WEEKDAY_MAP[get('weekday')] ?? date.getUTCDay(),
  };
}

function zonedLocalToUtc({
  year,
  month,
  day,
  hour,
  timezone,
}: {
  year: number;
  month: number;
  day: number;
  hour: number;
  timezone: string;
}): Date {
  let utc = new Date(Date.UTC(year, month - 1, day, hour, 0, 0, 0));

  for (let i = 0; i < 4; i += 1) {
    const parts = getZonedParts(utc, timezone);
    const asUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
      0,
    );
    const desired = Date.UTC(year, month - 1, day, hour, 0, 0, 0);
    const delta = desired - asUtc;

    if (delta === 0) {
      break;
    }

    utc = new Date(utc.getTime() + delta);
  }

  return utc;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

const MAX_DAY_BY_MONTH = [
  31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31,
] as const;

export function getMaxDayForMonth(month: number): number {
  if (month < 1 || month > 12) {
    return 0;
  }

  return MAX_DAY_BY_MONTH[month - 1] ?? 0;
}

export function isValidYearlyMonthDay({
  yearlyMonth,
  yearlyDay,
}: {
  yearlyMonth: number;
  yearlyDay: number;
}): boolean {
  return yearlyDay >= 1 && yearlyDay <= getMaxDayForMonth(yearlyMonth);
}

function addCalendarDays(
  year: number,
  month: number,
  day: number,
  delta: number,
): { year: number; month: number; day: number } {
  const utc = new Date(Date.UTC(year, month - 1, day + delta));

  return {
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate(),
  };
}

function getStartMonthsOfChosenPeriod({
  startMonth,
  intervalMonths,
}: {
  startMonth: number;
  intervalMonths: number;
}): number[] {
  return Array.from(
    { length: 12 / intervalMonths },
    (_, offset) => ((startMonth - 1 + offset * intervalMonths) % 12) + 1,
  );
}

function candidateDayForRule({
  rule,
  year,
  month,
}: {
  rule: ScheduledReportRuleInput;
  year: number;
  month: number;
}): number | null {
  switch (rule.frequency) {
    case 'WEEKLY':
      return null;
    case 'MONTHLY': {
      if (rule.lastDayOfMonth) {
        return daysInMonth(year, month);
      }

      if (rule.dayOfMonth == null) {
        return null;
      }

      const dim = daysInMonth(year, month);

      return rule.dayOfMonth <= dim ? rule.dayOfMonth : null;
    }

    case 'QUARTERLY':
    case 'SEMI_ANNUAL': {
      if (rule.fiscalYearStartMonth == null) {
        return null;
      }

      const startMonths = getStartMonthsOfChosenPeriod({
        startMonth: rule.fiscalYearStartMonth,
        intervalMonths: rule.frequency === 'QUARTERLY' ? 3 : 6,
      });

      return startMonths.includes(month) ? 1 : null;
    }

    case 'YEARLY': {
      if (
        rule.yearlyMonth == null ||
        rule.yearlyDay == null ||
        rule.yearlyMonth !== month
      ) {
        return null;
      }

      const dim = daysInMonth(year, month);

      return rule.yearlyDay <= dim ? rule.yearlyDay : null;
    }

    default: {
      const exhaustive: never = rule.frequency;

      return exhaustive;
    }
  }
}

function matchesWeeklyDay({
  rule,
  dayOfWeek,
}: {
  rule: ScheduledReportRuleInput;
  dayOfWeek: number;
}): boolean {
  return (rule.daysOfWeek ?? []).includes(dayOfWeek);
}

function getNextYearlyRunUtc({
  rule,
  after,
  timezone,
}: {
  rule: ScheduledReportRuleInput;
  after: Date;
  timezone: string;
}): Date | null {
  if (rule.yearlyMonth == null || rule.yearlyDay == null) {
    return null;
  }

  if (
    !isValidYearlyMonthDay({
      yearlyMonth: rule.yearlyMonth,
      yearlyDay: rule.yearlyDay,
    })
  ) {
    return null;
  }

  const afterParts = getZonedParts(after, timezone);

  for (
    let yearOffset = 0;
    yearOffset <= MAX_YEARLY_SEARCH_YEARS;
    yearOffset += 1
  ) {
    const year = afterParts.year + yearOffset;
    const dim = daysInMonth(year, rule.yearlyMonth);

    if (rule.yearlyDay > dim) {
      continue;
    }

    const occurrence = zonedLocalToUtc({
      year,
      month: rule.yearlyMonth,
      day: rule.yearlyDay,
      hour: rule.hour,
      timezone,
    });

    if (occurrence.getTime() > after.getTime()) {
      return occurrence;
    }
  }

  return null;
}

export function getNextScheduledReportRunUtc({
  rule,
  after = new Date(),
}: {
  rule: ScheduledReportRuleInput;
  after?: Date;
}): Date | null {
  if (rule.hour < 0 || rule.hour > 23) {
    return null;
  }

  const timezone = resolveTimezone(rule.timezone);

  if (rule.frequency === 'YEARLY') {
    return getNextYearlyRunUtc({ rule, after, timezone });
  }

  const afterParts = getZonedParts(after, timezone);
  let cursor = {
    year: afterParts.year,
    month: afterParts.month,
    day: afterParts.day,
  };

  for (let i = 0; i < MAX_SEARCH_DAYS; i += 1) {
    const weekdayProbe = zonedLocalToUtc({
      year: cursor.year,
      month: cursor.month,
      day: cursor.day,
      hour: 12,
      timezone,
    });
    const dayOfWeek = getZonedParts(weekdayProbe, timezone).dayOfWeek;

    const isWeeklyMatch =
      rule.frequency === 'WEEKLY' && matchesWeeklyDay({ rule, dayOfWeek });
    const candidateDay =
      rule.frequency === 'WEEKLY'
        ? isWeeklyMatch
          ? cursor.day
          : null
        : candidateDayForRule({
            rule,
            year: cursor.year,
            month: cursor.month,
          });

    if (candidateDay !== null && candidateDay === cursor.day) {
      const occurrence = zonedLocalToUtc({
        year: cursor.year,
        month: cursor.month,
        day: cursor.day,
        hour: rule.hour,
        timezone,
      });

      if (occurrence.getTime() > after.getTime()) {
        return occurrence;
      }
    }

    cursor = addCalendarDays(cursor.year, cursor.month, cursor.day, 1);
  }

  return null;
}

export function withComputedNextRuns({
  rules,
  after = new Date(),
}: {
  rules: ScheduledReportRuleInput[];
  after?: Date;
}): ScheduledReportRuleWithNextRun[] {
  return rules.map((rule) => ({
    ...rule,
    nextRunAt: getNextScheduledReportRunUtc({ rule, after }),
  }));
}

export function getSoonestNextRun({
  rules,
}: {
  rules: ScheduledReportRuleInput[];
}): Date | null {
  const candidates = rules
    .map((rule) => {
      if (rule.nextRunAt != null) {
        return rule.nextRunAt instanceof Date
          ? rule.nextRunAt
          : new Date(rule.nextRunAt);
      }

      return getNextScheduledReportRunUtc({ rule });
    })
    .filter((value): value is Date => value != null);

  if (candidates.length === 0) {
    return null;
  }

  return new Date(Math.min(...candidates.map((date) => date.getTime())));
}

export function advanceScheduledReportRule({
  rule,
  ranAt = new Date(),
}: {
  rule: ScheduledReportRuleInput;
  ranAt?: Date;
}): ScheduledReportRuleWithNextRun {
  return {
    ...rule,
    lastRunAt: ranAt,
    nextRunAt: getNextScheduledReportRunUtc({ rule, after: ranAt }),
  };
}

export function getScheduledReportPeriodLabel({
  frequency,
}: {
  frequency: ScheduledReportFrequency;
}): string {
  switch (frequency) {
    case 'WEEKLY':
      return 'Weekly';
    case 'MONTHLY':
      return 'Monthly';
    case 'QUARTERLY':
      return 'Quarterly';
    case 'SEMI_ANNUAL':
      return 'Semiannual';
    case 'YEARLY':
      return 'Yearly';
    default: {
      const exhaustive: never = frequency;

      return exhaustive;
    }
  }
}

export function buildScheduledReportTitle({
  applicationName,
  frequency,
  reportDate = new Date(),
}: {
  applicationName: string;
  frequency: ScheduledReportFrequency;
  reportDate?: Date;
}): string {
  const period = getScheduledReportPeriodLabel({ frequency });
  const dateLabel = reportDate.toISOString().slice(0, 10);

  return `${applicationName} — Scheduled Security Assessment — ${period}—${dateLabel}`;
}
